import { useEffect, useMemo, useState } from 'react'
import { Alert, Descriptions, Modal, Space, Spin, Tag, TreeSelect, Typography } from 'antd'
import type { TreeSelectProps } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useSubscriberCountUnder } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  ancestorChain,
  buildOrgTree,
  canPlaceUnder,
  collectSubtreeIds,
  fetchOrgSubtree,
  kindName,
  moveOrgNode,
  orgTypeName,
  typeOf,
  type OrgNode,
  type OrgNodeType,
  type OrgTreeNode,
} from './orgApi'

/**
 * "Ildizga chiqarish" uchun sun'iy qiymat.
 *
 * `TreeSelect` `null` ni "tanlanmagan" deb tushunadi, ya'ni ildizga
 * chiqarishni bo'sh maydon bilan ifodalab bo'lmasdi: operator "hech nima
 * tanlamadim" va "ataylab ildizga chiqaryapman" ni farqlay olmasdi.
 */
const ROOT_VALUE = '__root__'

/**
 * `TreeSelect` ning tugun tipi — antd'ning O'ZIDAN olinadi.
 *
 * ⚠️ `antd/es/select` dagi `DefaultOptionType` YARAMAYDI: unda `title`
 * faqat `string` va `value` `null` bo'lishi mumkin. Bu yerda sarlavha
 * JSX (nom + tur + taqiq sababi), ya'ni tipni qo'lda yozish o'rniga
 * komponentning haqiqiy shartnomasidan olinadi — u antd yangilanganda
 * ham mos qoladi.
 */
type OrgTreeSelectNode = NonNullable<TreeSelectProps['treeData']>[number]

interface OrgNodeMoveModalProps {
  open: boolean
  node: OrgNode
  /** Butun ko'rinadigan daraxt — nomzod otalar va sikl tekshiruvi uchun */
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  onClose: () => void
}

/**
 * Daraxtda KO'CHIRISH — panelning eng xavfli amali.
 *
 * <h2>⚠️ Nega alohida modal va nega tasdiqlash bilan</h2>
 * Ko'chirish bitta maydonni almashtirmaydi. U:
 *   1. butun shoxning `path` ini qayta hisoblaydi
 *      (`org_node_path_cascade`);
 *   2. abonentlardagi nusxani — `subscriber.reseller_path` ni — kaskad
 *      bilan yangilaydi;
 *   3. ya'ni `SUBTREE` doiradagi operatorlar KO'RADIGAN abonentlar
 *      to'plamini o'zgartiradi.
 *
 * `QARORLAR.md` §19.8 aynan shu haqda: "reseller o'z abonentlarini
 * ko'rmay qoladi, yoki begonalarni ko'radi. Va bu HECH QANDAY xato
 * bermaydi". Shuning uchun oqibat tugmani bosishdan OLDIN raqam bilan
 * ko'rsatiladi.
 *
 * <h2>⚠️ Nomzod otalar ro'yxati IMKONIYAT bo'yicha filtrlanadi</h2>
 * `QARORLAR.md` §19.3: tur `allowed_parents` ni O'ZI aytadi. Kodda
 * "ombor agent ostida turmaydi" degan shart YO'Q — ro'yxat turning
 * ma'lumotidan quriladi. Bu QULAYLIK: haqiqiy qo'riqchi bazada
 * (`org_node_path_guard`, 4-qadam), va u migratsiya bilan qo'lda
 * yozilgan `UPDATE` ni ham qamraydi.
 */
export function OrgNodeMoveModal({
  open,
  node,
  nodes,
  types,
  onClose,
}: OrgNodeMoveModalProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const [target, setTarget] = useState<string | null>(null)

  const movedType = typeOf(types, node.kind)

  // Har ochilishda tanlov TOZALANADI: modal bir tugun uchun ochilib,
  // yopilib, boshqasi uchun qayta ochilganda eski nishon qolib ketardi.
  useEffect(() => {
    if (open) setTarget(null)
  }, [open, node.id])

  /**
   * Ta'sirlanadigan TUGUNLAR — serverdan.
   *
   * ⚠️ Mahalliy daraxtdan sanash mumkin edi, lekin u FAQAT ko'rinish
   * doirasidagi qismni biladi. `SUBTREE` doiradagi operator uchun ikkala
   * son bir xil chiqadi, `TENANT` doirada esa server javobi to'liqroq.
   * "Nechta tugun o'zgaradi" degan savolga yarim javob berish — eng
   * yomon variant.
   */
  const subtree = useQuery({
    queryKey: queryKeys.org.subtree(node.id),
    queryFn: () => fetchOrgSubtree(node.id),
    enabled: open,
    staleTime: 30_000,
  })

  /**
   * Ta'sirlanadigan ABONENTLAR.
   *
   * ⚠️ So'rov FAQAT `settlementParty` turlarida yuboriladi va bu ataylab
   * — sabab `useSubscriberCountUnder` javadoc'ida: filtr yo'lni
   * `is_settlement_party` bo'yicha FILTRLANGAN moslik VIEW idan oladi,
   * ya'ni ombor yoki filial id'si berilsa javob JIMGINA `0` bo'ladi.
   * Nolni ko'rsatish "hech kim ta'sirlanmaydi" degan YOLG'ON bo'lardi.
   *
   * ⚠️ Shart tur NOMIGA emas, IMKONIYATIGA qaraydi (§19.3).
   */
  const countable = movedType?.settlementParty === true
  const mayCount = can('subscriber.read')
  const subscribers = useSubscriberCountUnder(node.id, open && countable && mayCount)

  /**
   * Hisob olinmaydigan holatda — shox ichida nechta hisob-kitob tuguni
   * borligi. Bu abonent soni EMAS, lekin "abonentlar ta'sirlanishi
   * mumkinmi" degan savolga rost javob beradi.
   */
  const settlementDescendants = useMemo(() => {
    const ids = collectSubtreeIds(nodes, node.id)
    return nodes.filter(
      (item) => ids.has(item.id) && typeOf(types, item.kind)?.settlementParty === true,
    ).length
  }, [nodes, types, node.id])

  /**
   * Nomzod otalar daraxti.
   *
   * O'chirilgan (tanlab bo'lmaydigan) tugunlar YASHIRILMAYDI, chunki
   * ular ostidagilar tanlanishi mumkin — daraxtning shakli buzilmasligi
   * kerak. Sababi esa tooltip'da emas, tugunning yonida yoziladi.
   */
  const forbidden = useMemo(() => collectSubtreeIds(nodes, node.id), [nodes, node.id])

  /** Qidiruv uchun matn — sarlavha JSX bo'lgani uchun alohida xarita */
  const searchText = useMemo(
    () => new Map(nodes.map((item) => [item.id, `${item.displayName} ${item.code}`.toLowerCase()])),
    [nodes],
  )

  const treeData = useMemo<OrgTreeSelectNode[]>(() => {
    const convert = (item: OrgTreeNode): OrgTreeSelectNode => {
      // ⚠️ IKKI xil taqiq, IKKI xil sabab — va operator qaysi biri
      // ekanini bilishi kerak:
      //   · o'z shoxi        → sikl (baza `23514` bilan rad etadi)
      //   · turga to'g'ri kelmaydi → `allowed_parents`
      const cycle = forbidden.has(item.id)
      const placement = !canPlaceUnder(movedType, item.kind)
      const reason = cycle
        ? t('org.move.reasonCycle')
        : placement
          ? t('org.move.reasonPlacement', { type: movedType ? orgTypeName(movedType, t) : node.kind })
          : null

      return {
        value: item.id,
        title: (
          <Space size={4}>
            <span>{item.displayName}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {kindName(types, item.kind, t)}
            </Typography.Text>
            {reason ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                ({reason})
              </Typography.Text>
            ) : null}
          </Space>
        ),
        disabled: cycle || placement,
        children: item.children.map(convert),
      }
    }

    return [
      {
        value: ROOT_VALUE,
        // ⚠️ Ildiz HAR DOIM mumkin — `allowed_parents` dan qat'i nazar
        // (`org_node_path_guard`, 3-qadam).
        title: (
          <Space size={4}>
            <span>{t('org.move.toRoot')}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('org.move.toRootHint')}
            </Typography.Text>
          </Space>
        ),
      },
      ...buildOrgTree(nodes).map(convert),
    ]
  }, [nodes, forbidden, movedType, types, node.kind, t])

  const targetId = target === ROOT_VALUE ? null : target
  const unchanged = target !== null && targetId === node.parentId

  const currentChain = ancestorChain(nodes, node.parentId)
  const nextChain = ancestorChain(nodes, targetId)

  const move = useMutation({
    mutationFn: () => moveOrgNode(node.id, targetId),
    onSuccess: () => {
      // ⚠️ `org.all` PREFIKS: daraxt ham, shox so'rovi ham eskiradi.
      void queryClient.invalidateQueries({ queryKey: queryKeys.org.all })
      // ⚠️ Abonentlar ham: ularning `reseller_path` i kaskad bilan
      // o'zgardi, ya'ni doiraga bog'liq har qanday ro'yxat eskirgan.
      // Buni unutish "ko'chirdim, lekin ro'yxat o'zgarmadi" degan
      // ziddiyatni ekranda qoldirardi.
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
      onClose()
    },
  })

  const affectedNodes = subtree.data?.length ?? null

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    move.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('org.move.title', { name: node.displayName })}
      onCancel={close}
      onOk={() => move.mutate()}
      confirmLoading={move.isPending}
      okText={t('org.move.confirm')}
      okButtonProps={{ danger: true, disabled: target === null || unchanged }}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={680}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {move.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(move.error, t)}
        />
      ) : null}

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('org.move.warningTitle')}
        description={t('org.move.warningBody')}
      />

      <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('org.move.affectedNodes')}>
          {subtree.isPending ? (
            <Spin size="small" />
          ) : subtree.isError ? (
            <Typography.Text type="warning">{t('org.move.affectedUnknown')}</Typography.Text>
          ) : (
            <Typography.Text strong>
              {t('org.move.affectedNodesValue', { count: affectedNodes ?? 0 })}
            </Typography.Text>
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t('org.move.affectedSubscribers')}>
          {/*
            UCH XIL JAVOB, va ularni bir-biriga aylantirish TAQIQLANADI:
              · aniq son      — so'rov yuborildi va u shu tur uchun to'g'ri
              · "noma'lum"    — server bu tur uchun hisob bermaydi
              · "huquq yo'q"  — operator abonentlarni ko'ra olmaydi
            Ikkinchi va uchinchisini `0` bilan almashtirish jimgina
            yolg'on bo'lardi.
          */}
          {!mayCount ? (
            <Typography.Text type="secondary">
              {t('org.move.subscribersNoPermission')}
            </Typography.Text>
          ) : !countable ? (
            <Typography.Text type="warning">
              {t('org.move.subscribersNotCountable', { count: settlementDescendants })}
            </Typography.Text>
          ) : subscribers.isLoading ? (
            <Spin size="small" />
          ) : subscribers.isError || subscribers.count === null ? (
            <Typography.Text type="warning">{t('org.move.affectedUnknown')}</Typography.Text>
          ) : (
            <Typography.Text strong>
              {t('org.move.affectedSubscribersValue', { count: subscribers.count })}
            </Typography.Text>
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t('org.move.currentParent')}>
          {currentChain.length === 0 ? (
            <Tag>{t('org.move.toRoot')}</Tag>
          ) : (
            currentChain.map((item) => item.displayName).join(' › ')
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t('org.move.newParent')}>
          {target === null ? (
            <Typography.Text type="secondary">{t('org.move.notSelected')}</Typography.Text>
          ) : nextChain.length === 0 ? (
            <Tag color="blue">{t('org.move.toRoot')}</Tag>
          ) : (
            <Typography.Text strong>
              {nextChain.map((item) => item.displayName).join(' › ')}
            </Typography.Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Text type="secondary">{t('org.move.pickParent')}</Typography.Text>
      {/*
        ⚠️ `allowClear` ATAYLAB YO'Q. Tozalash `onChange(undefined)`
        beradi, undefined esa so'rov tanasida `parentId` ni butunlay
        yo'qotardi va server uni `null` — ya'ni ILDIZGA CHIQARISH deb
        o'qirdi. "Tanlovni bekor qildim" degan harakat jimgina
        ko'chirishga aylanardi. Ildizga chiqarish uchun ro'yxatning
        birinchi bandi bor va u ANIQ nomlangan.

        ⚠️ `treeNodeFilterProp="title"` ham yaramaydi: sarlavha JSX,
        matn emas. Qidiruv shuning uchun nom va kod ustidan alohida
        xarita bo'yicha ishlaydi — aks holda qidiruv maydoni bosilardi,
        lekin hech nima topmasdi.
      */}
      <TreeSelect
        value={target ?? undefined}
        onChange={(value: string | undefined) => setTarget(value ?? null)}
        treeData={treeData}
        treeDefaultExpandAll
        showSearch
        filterTreeNode={(input, treeNode) => {
          const value = String(treeNode.value ?? '')
          if (value === ROOT_VALUE) return true
          return (searchText.get(value) ?? '').includes(input.trim().toLowerCase())
        }}
        placeholder={t('org.move.pickParentPlaceholder')}
        style={{ width: '100%', marginTop: 8 }}
      />

      {unchanged ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message={t('org.move.unchanged')}
        />
      ) : null}
    </Modal>
  )
}
